import type {AssistantTimelineV2, TimelineEntry, TimelineSegmentKind} from './hooks'

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isNaN(n) ? fallback : n
  }
  return fallback
}

/** Coerce one timeline entry from wire (snake_case or camelCase) to v2 `TimelineEntry`. */
export function coerceTimelineEntry(raw: unknown): TimelineEntry | null {
  if (!raw || typeof raw !== 'object') return null
  const e = raw as Record<string, unknown>
  const kind = e.kind
  const step_index = num(e.step_index ?? e.stepIndex, 0)

  if (kind === 'text') {
    let sk = (e.segment_kind ?? e.segmentKind) as string | undefined
    if (sk !== 'preamble' && sk !== 'reasoning' && sk !== 'final') {
      sk = 'reasoning'
    }
    if (typeof e.content !== 'string') return null
    return {
      kind: 'text',
      segment_kind: sk as TimelineSegmentKind,
      content: e.content,
      step_index,
    }
  }

  if (kind === 'tool_call') {
    const tool_call_id = (e.tool_call_id ?? e.toolCallId) as string | undefined
    const tool_name = (e.tool_name ?? e.toolName) as string | undefined
    if (!tool_call_id || !tool_name) return null
    const tool_input =
      (e.tool_input ?? e.toolInput) && typeof (e.tool_input ?? e.toolInput) === 'object'
        ? ((e.tool_input ?? e.toolInput) as Record<string, unknown>)
        : {}
    const display_summary = (e.display_summary ?? e.displaySummary) as string | undefined
    const skill_name = (e.skill_name ?? e.skillName) as string | undefined
    return {
      kind: 'tool_call',
      tool_call_id,
      tool_name,
      tool_input,
      ...(display_summary ? {display_summary} : {}),
      ...(skill_name ? {skill_name} : {}),
      step_index,
    }
  }

  if (kind === 'tool_result') {
    const tool_call_id = (e.tool_call_id ?? e.toolCallId) as string | undefined
    const tool_name = (e.tool_name ?? e.toolName) as string | undefined
    if (!tool_call_id || !tool_name) return null
    const content = typeof e.content === 'string' ? e.content : ''
    const ok = e.ok !== false
    const error = (e.error as string | null | undefined) ?? null
    return {
      kind: 'tool_result',
      tool_call_id,
      tool_name,
      content,
      ok,
      error,
      step_index,
    }
  }

  return null
}

/**
 * Normalize wire/API timeline payloads to v2 `AssistantTimelineV2`.
 * Accepts `schema_version` as number 2 or string "2"; coerces each entry from camelCase/snake_case.
 */
export function normalizeAssistantTimeline(raw: unknown): AssistantTimelineV2 | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  const sv = o.schema_version ?? o.schemaVersion
  const schemaOk = sv === 2 || sv === '2'
  if (!schemaOk || !Array.isArray(o.entries)) return undefined
  const entries = o.entries.map(coerceTimelineEntry).filter((x): x is TimelineEntry => x !== null)
  return {schema_version: 2, entries}
}

/** Stable key for deduping text rows across server/local timelines. */
export function timelineTextSignature(e: TimelineEntry): string | null {
  if (e.kind !== 'text') return null
  const t = e.content.trim().slice(0, 240)
  return `${e.step_index}|${e.segment_kind}|${t.length}|${t}`
}

/**
 * Export v2 timeline entries as markdown `<details>` blocks (for chat export).
 */
export function timelineEntriesToExportLines(entries: TimelineEntry[]): string[] {
  const lines: string[] = []
  for (const e of entries) {
    if (e.kind === 'text') {
      const body = e.content.trim()
      if (!body) continue
      const label = e.segment_kind === 'preamble' ? 'Preamble' : e.segment_kind === 'final' ? 'Final' : 'Reasoning'
      lines.push('<details>')
      lines.push(`<summary>${label} (step ${e.step_index})</summary>`)
      lines.push('')
      lines.push(body)
      lines.push('')
      lines.push('</details>')
      lines.push('')
      continue
    }
    if (e.kind === 'tool_call') {
      lines.push('<details>')
      lines.push(`<summary>Tool: ${e.tool_name} (${e.tool_call_id})</summary>`)
      lines.push('')
      lines.push('```json')
      lines.push(JSON.stringify(e.tool_input ?? {}, null, 2))
      lines.push('```')
      lines.push('')
      lines.push('</details>')
      lines.push('')
      continue
    }
    if (e.kind === 'tool_result') {
      lines.push('<details>')
      lines.push(`<summary>Tool result: ${e.tool_name} (${e.tool_call_id})</summary>`)
      lines.push('')
      lines.push(e.ok ? '' : `_Error: ${e.error ?? 'unknown'}_\n\n`)
      lines.push('```')
      lines.push((e.content ?? '').trim() || '(empty)')
      lines.push('```')
      lines.push('')
      lines.push('</details>')
      lines.push('')
    }
  }
  return lines
}
